import React, { FC, useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  IconBtn,
  ModuleTitleText,
  UiInput,
  UiSelect,
  UiDatePicker,
} from '@ru-digital-assets/ui-component';
import { useFormik } from 'formik';
import DetailsContainer from '../../../containers/details-container';
import FormFooter from '../../../containers/form-footer';
import AppPageMain from '../../../modules/app-page/app-page--main';
import PageHeader from '../../../modules/page-header';
import { OptionType } from '../../../modules/ui/ui-select/ui-select';
import { useAppDispatch } from '../../../store';
import { initSale } from '../../../store/actions/operations-sign';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';

import { getSale } from 'store/actions/operations-sign';

import SaleIcon from './icons/sale-icon.svg';
import '../cfa-release/index.scss';

type FormStateType = {
  accountId?: number;
  amount?: number;
  companyId?: number;
  tokenCount?: number;
  tokenName?: string;
  personal?: boolean;
  expiryDate?: string;
};

const initialFormState: FormStateType = {};

const AssetsSellingPage: FC = () => {
  const history = useHistory();
  const id = history.location.state?.id;
  const dispatch = useAppDispatch();
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [limitCount, setLimitTokenCount] = useState(-1);
  const [validTokenCount, setValidTokenCount] = useState(true);

  const paymentTypes = useSelector(
    (state: RootState) => state.commonInfo.availablePaymentTokens
  );
  const accountId = useSelector((state: RootState) => state.auth.account.id);
  const companyInfos = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );

  const paymentOptionTypes = useMemo(
    () =>
      paymentTypes.map((item) => ({
        value: item.code,
        label: item.name + ' (доступно: ' + item.actualCount + ')',
        limit: item.actualCount,
      })),
    [paymentTypes]
  );

  const companies: OptionType[] = useMemo(() => {
    const arr = [];
    for (let key in companyInfos) {
      if (accountId.toString() !== key) {
        arr.push({
          value: key,
          label: `${companyInfos[key].shortName} (ИНН ${companyInfos[key].tin})`,
        });
      }
    }
    return arr;
  }, [companyInfos]);

  const formState = useFormik({
    initialValues: initialFormState,
    onSubmit: (values) => {
      setLoadingBtn(true);
      dispatch(initSale({ ...values, accountId }))
        .then(() => {
          setLoadingBtn(false);
          history.push('/assets/signing-sale');
        })
        .catch(() => {
          setLoadingBtn(false);
        });
    },
  });

  useEffect(() => {
    if (id) {
      dispatch(getSale({ id }));
    }
  }, [id]);

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                { link: '/assets', title: 'Активы' },
                { link: '#', title: 'Продажа' },
              ]}
              iconType="assets"
            />
          </div>
        </div>
        <div className="buying-container">
          <DetailsContainer
            title="Продажа цифровых финансовых активов"
            onClose={() => history.back()}
            infoTitle={{
              steps: [
                { title: 'Параметры', active: true },
                { title: 'Подпись' },
                { title: 'Согласование' },
                { title: 'Подтвеждение оплаты' },
              ],
            }}
            leftComponent={
              <form className="form-assets">
                <div className="form-assets__item">
                  <p className="p p_font-size_14 p_color_grey mb_8">
                    Выберите токен для продажи
                  </p>
                  <div className="form-item">
                    <UiSelect
                      placeholder="Выберите токен из списка"
                      options={paymentOptionTypes}
                      lg
                      onChange={(val) => {
                        formState.setFieldValue(
                          'tokenName',
                          (val as OptionType)?.value
                        );
                        setLimitTokenCount(Number((val as OptionType)?.limit));
                      }}
                    />
                    {limitCount > 0 && (
                      <p className="text">
                        Срок исполнения требований
                      </p>
                    )}
                  </div>
                </div>
                <div className="form-assets__item flex form-assets__token-count">
                  <UiInput
                    required
                    className="input-left"
                    rightText="шт"
                    name="tokenCount"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      if (e.target.value) {
                        let count = Number(e.target.value.replace(/\s/g, ''));
                        formState.setFieldValue('tokenCount', count);
                        if (limitCount >= count) {
                          setValidTokenCount(true);
                        } else {
                          setValidTokenCount(false);
                        }
                      } else {
                        formState.setFieldValue('tokenCount', null);
                      }
                    }}
                    lg
                    label={'Количество токенов'}
                    error={!validTokenCount}
                  />
                  <div
                    className="flex justify-center align-center"
                    style={{ width: 'auto' }}
                  >
                    X
                  </div>
                  <UiInput
                    required
                    className="input-right"
                    rightText="₽"
                    name="amount"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formState.setFieldValue(
                        'amount',
                        e.target.value
                          ? Number(e.target.value.replace(/\s/g, ''))
                          : null
                      );
                    }}
                    lg
                    label="Цена продажи одного токена"
                  />
                </div>
                {!validTokenCount && (
                  <div className="form-item__footer">
                    <span className="red">
                      Значение больше, чем доступное число токенов ({limitCount}
                      )
                    </span>
                    <br />
                    <br />
                  </div>
                )}
                <div className="form-assets__item">
                  <p className="p p_font-size_14 p_color_grey mb_8">
                    Укажите Покупателя для осуществления сделки
                  </p>
                  <div className="form-item">
                    <UiSelect
                      placeholder="Покупатель"
                      options={companies}
                      onChange={(val) => {
                        formState.setFieldValue(
                          'companyId',
                          Number((val as OptionType)?.value)
                        );
                      }}
                    />
                  </div>
                </div>
                <p className="p p_font-size_14 p_color_grey mb_8">
                  Срок для оплаты токенов
                </p>
                <div className="form-assets__expire-date">
                  <div>
                    <UiDatePicker
                      selected={formState.values.expiryDate}
                      minDate={new Date()}
                      dateFormat="dd.MM.yyyy"
                      onChange={(value: string) =>
                        formState.setFieldValue('expiryDate', value)
                      }
                    />
                  </div>
                  <p className="p p_font-size_12 p_color_grey">
                    Срок, в течение которого заявка должна быть акцептована
                    Покупателем и оплачена. Если токены не будут оплачены в
                    течение этого срока, сделка будет отменена.
                  </p>
                </div>
                <FormFooter>
                  <div className="flex justify-end">
                    <div className="form-assets__flex">
                      <div className="form-assets__item form-assets__item--sm">
                        <ModuleTitleText
                          top={
                            formState.values &&
                            formState.values.tokenCount &&
                            formState.values.amount
                              ? 'Вы продаете цифровые финансовые активы на сумму'
                              : ''
                          }
                          bottom={
                            formState.values &&
                            formState.values.tokenCount &&
                            formState.values.amount
                              ? currencyFormat(
                                  formState.values.amount *
                                    formState.values.tokenCount
                                )
                              : ''
                          }
                        />
                      </div>
                    </div>
                    <IconBtn
                      disabled={
                        loadingBtn ||
                        !validTokenCount ||
                        !formState.values.amount ||
                        !formState.values.companyId ||
                        !formState.values.tokenCount ||
                        !formState.values.tokenName ||
                        !formState.isValid ||
                        !formState.values.expiryDate
                      }
                      onClick={formState.handleSubmit}
                      icon={SaleIcon}
                      title="Сформировать заявку о продаже ЦФА"
                    />
                  </div>
                </FormFooter>
              </form>
            }
          />
        </div>
      </div>
    </AppPageMain>
  );
};

export default AssetsSellingPage;
